import React, { useState, useRef, useEffect } from 'react';
import { useGetPublicNewsQuery, useGetPublicNewsActivityQuery, useGetThreatOverviewQuery } from '@/lib/services/threatFeedsApi';
import IntelActivityBarChart from '@/components/features/threat-feeds/IntelActivityBarChart';
import { Filter, RotateCcw, ChevronDown, Search, AlertTriangle, X, Info, Copy, CheckCircle2 } from 'lucide-react';

// --- CUSTOM DROPDOWN COMPONENT ---
interface DropdownProps {
    label: string;
    value: string;
    options: string[];
    onChange: (val: string) => void;
    searchable?: boolean;
}

const FilterDropdown: React.FC<DropdownProps> = ({ label, value, options, onChange, searchable = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(query.toLowerCase()));

    return (
        <div className="relative" ref={dropdownRef}>
            <div onClick={() => setIsOpen(!isOpen)} className={`bg-[#0A0B0E] border ${isOpen ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center justify-between min-w-[120px] cursor-pointer hover:border-slate-500 transition-colors`}>
                <span>{label}: <span className="text-white ml-1">{value}</span></span>
                <ChevronDown className={`w-3 h-3 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#50A970]' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[160px] bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.8)] z-50 overflow-hidden flex flex-col">
                    {searchable && (
                        <div className="p-2 border-b border-[#1A1D24]">
                            <div className="bg-[#13171F] rounded flex items-center px-2 py-1.5">
                                <Search className="w-3 h-3 text-slate-500 mr-2 shrink-0" />
                                <input type="text" placeholder="Type to filter" value={query} onChange={(e) => setQuery(e.target.value)} className="bg-transparent border-none text-[11px] text-white focus:outline-none w-full placeholder-slate-600" />
                            </div>
                        </div>
                    )}
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-1">
                        <div onClick={() => { onChange(`All`); setIsOpen(false); setQuery(''); }} className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value === 'All' ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}>
                            All
                        </div>
                        {filteredOptions.map((opt) => (
                            <div key={opt} onClick={() => { onChange(opt); setIsOpen(false); setQuery(''); }} className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value === opt ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}>
                                {opt}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- MOCK TAGS ---
const COMMON_TAGS = ['ddos', 'ransomware', 'malware', 'phishing', 'breach', 'vulnerability', 'botnet', 'zero-day'];

// --- MAIN COMPONENT ---
const PublicNews: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({ search: '', date: 'All', tags: 'All' });

    // Modals State
    const [selectedNews, setSelectedNews] = useState<any | null>(null); // For the AI Summary details modal
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);  // For the OPSEC warning modal
    const [copied, setCopied] = useState(false); // To show feedback when link is copied

    const isFilterActive = filters.search !== '' || filters.date !== 'All' || filters.tags !== 'All';

    // 1. Compute Query Parameters
    const queryParams: Record<string, any> = { limit: 100 };

    const combinedSearch = [filters.search, filters.tags !== 'All' ? filters.tags : ''].filter(Boolean).join(' ');
    queryParams.search = combinedSearch || 'cyber'; // Fallback to satisfy backend

    if (filters.date !== 'All') {
        const days = parseInt(filters.date.split(' ')[0]);
        if (!isNaN(days)) {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - days);
            queryParams.start_date = startDate.toISOString().split('T')[0];
            queryParams.end_date = new Date().toISOString().split('T')[0];
        }
    }

    // 2. Fetch Data
    const { data: newsData, isLoading: isNewsLoading } = useGetPublicNewsQuery(queryParams);
    const { data: activityData, isLoading: isActivityLoading } = useGetPublicNewsActivityQuery({ search: queryParams.search });
    const { data: overviewData, isLoading: isOverviewLoading } = useGetThreatOverviewQuery();

    const newsResults = newsData?.results || [];

    const lastSyncString = overviewData?.meta?.last_sync_utc ? `${overviewData.meta.last_sync_utc} UTC` : 'AWAITING TELEMETRY...';
    const systemStatus = overviewData?.status ? overviewData.status.toUpperCase() : 'ELEVATED';
    const statusColorClass = systemStatus === 'SUCCESS' ? 'text-[#50A970]' : 'text-[#FF3333]';

    // 3. Handlers
    const handleSearchClick = () => setFilters(prev => ({ ...prev, search: searchInput }));
    const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && handleSearchClick();
    const handleReset = () => {
        setSearchInput('');
        setFilters({ search: '', date: 'All', tags: 'All' });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset icon after 2s
    };

    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative">

            {/* Header */}
            <div className="flex justify-between items-end mb-2">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Threats Feeds</h1>
                    <p className={`text-[10px] mt-1 font-semibold uppercase tracking-widest ${statusColorClass}`}>
                        System_Status: {isOverviewLoading ? 'SYNCING...' : systemStatus}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {isOverviewLoading ? 'Fetching...' : lastSyncString}</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex items-center gap-3 w-full relative z-30">
                <input
                    type="text"
                    placeholder="Search news titles and summaries..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 bg-[#0A0B0E] border border-[#1A1D24] text-sm text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#50A970] transition-colors placeholder-slate-600"
                />
                <button onClick={handleSearchClick} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-6 py-2.5 rounded-lg transition-colors text-[13px]">
                    Search
                </button>
                <button className={`border px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2 pointer-events-none ${isFilterActive ? 'bg-[#50A970]/10 border-[#50A970] text-[#50A970]' : 'bg-[#0A0B0E] border-[#1A1D24] text-slate-300 opacity-50'}`}>
                    <Filter className="w-4 h-4" /> {isFilterActive ? 'Filters Active' : 'Filters'}
                </button>
                <FilterDropdown label="Date" value={filters.date} options={['7 Days', '30 Days', '90 Days']} onChange={(val) => setFilters(prev => ({ ...prev, date: val }))} />
                <FilterDropdown label="Tags" value={filters.tags} options={COMMON_TAGS} onChange={(val) => setFilters(prev => ({ ...prev, tags: val }))} searchable={true} />
                <button onClick={handleReset} className="bg-[#0A0B0E] border border-[#1A1D24] hover:bg-[#161A23] text-slate-300 px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" /> Reset All
                </button>
            </div>

            {/* News List Section */}
            <div className="flex flex-col gap-4 relative z-10 mt-2">
                {isNewsLoading ? (
                    [...Array(3)].map((_, i) => (
                        <div key={i} className="bg-[#0A0B0E] border border-[#1A1D24] p-6 rounded-xl animate-pulse flex flex-col gap-3">
                            <div className="flex justify-between w-full"><div className="h-3 w-20 bg-slate-800 rounded"></div><div className="h-3 w-24 bg-slate-800 rounded"></div></div>
                            <div className="h-5 w-1/2 bg-slate-800 rounded mt-1"></div>
                            <div className="flex gap-2 mt-2"><div className="h-5 w-20 bg-slate-800 rounded"></div><div className="h-5 w-24 bg-slate-800 rounded"></div></div>
                        </div>
                    ))
                ) : newsResults.length === 0 ? (
                    <div className="py-12 flex items-center justify-center text-xs text-slate-600 border border-dashed border-[#1A1D24] rounded-xl">
                        No public news articles match the current search filters.
                    </div>
                ) : (
                    newsResults.map((news) => (
                        <div
                            key={news.id}
                            onClick={() => setSelectedNews(news)} // Opens details modal instead of directly linking
                            className="bg-[#0A0B0E] border border-[#1A1D24] hover:border-slate-700 transition-all p-6 rounded-xl flex flex-col gap-2 cursor-pointer group relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-[#50A970]/0 to-[#50A970]/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>

                            <div className="flex justify-between items-start relative z-10">
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                                    {news.published_at?.split('T')[0] || news.published_at?.split(' ')[0] || 'Unknown Date'}
                                </div>
                                <div className="text-[10px] font-bold text-[#4ADE80] opacity-80 uppercase tracking-widest">
                                    {news.source}
                                </div>
                            </div>

                            <h3 className="text-[16px] font-bold text-white tracking-wide mt-1 relative z-10">
                                {news.title}
                            </h3>

                            {news.tags && (
                                <div className="flex flex-wrap items-center gap-2 mt-3 relative z-10">
                                    {news.tags.split(',').map(tag => tag.trim()).filter(t => t).slice(0, 5).map((tag, idx) => (
                                        <span key={idx} className="bg-[#161A23] text-slate-400 text-[10px] px-2.5 py-1 rounded font-semibold tracking-wider">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* News Activity Histogram */}
            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg h-[320px] flex flex-col relative mt-2 z-10">
                <div className="flex justify-between items-end mb-6 z-10">
                    <h2 className="text-[14px] font-bold tracking-wide text-white">News Activity</h2>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                        Range: All-time
                    </div>
                </div>
                <div className="flex-1 relative">
                    <IntelActivityBarChart data={activityData?.news_activity_timeline} isLoading={isActivityLoading} />
                </div>
            </div>

            {/* --- INTERMEDIATE MODAL: ARTICLE DETAILS & AI SUMMARY --- */}
            {selectedNews && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 ml-72">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(0,0,0,0.9)] rounded-2xl w-full max-w-[500px] max-h-[90vh] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                        {/* Header */}
                        <div className="p-6 pb-5 border-b border-[#1A1D24]/60 relative shrink-0 overflow-hidden bg-gradient-to-b from-[#13171F] to-transparent">
                            {/* Subtle Circle Pattern */}
                            <svg className="absolute -top-4 -left-4 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.03] pointer-events-none" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="30" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="20" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="10" fill="none" stroke="white" strokeWidth="1.5" />
                            </svg>

                            <button onClick={() => setSelectedNews(null)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                                <X className="w-5 h-5" />
                            </button>

                            <div className="w-12 h-12 border border-[#1A1D24] bg-[#0A0B0E] rounded-full flex items-center justify-center mb-4 relative z-10 shadow-inner">
                                <Info className="w-5 h-5 text-slate-300" />
                            </div>

                            <h2 className="text-[18px] font-bold text-white mb-2 leading-tight pr-8 relative z-10">{selectedNews.title}</h2>
                            <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest relative z-10">
                                Public News
                            </span>
                        </div>

                        {/* Scrollable Body */}
                        <div className="p-6 flex flex-col gap-5 overflow-y-auto custom-scrollbar flex-1 bg-[#0A0B0E]">

                            {/* Metadata Block */}
                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-5 shadow-lg">
                                <h3 className="text-[13px] font-bold text-white mb-4 tracking-wide">Metadata</h3>
                                <div className="flex flex-col gap-3 font-mono text-[11px]">
                                    <div className="flex justify-between items-center border-b border-[#1A1D24]/50 pb-2">
                                        <span className="text-slate-500">Source</span>
                                        <span className="text-slate-300 truncate max-w-[200px] text-right">{selectedNews.source || 'Unknown'}</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-[#1A1D24]/50 pb-2">
                                        <span className="text-slate-500">Published</span>
                                        <span className="text-slate-300">{selectedNews.published_at?.split('T')[0] || 'Unknown'}</span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-slate-500 mt-1">Tags</span>
                                        <div className="flex flex-wrap justify-end gap-1.5 max-w-[200px]">
                                            {selectedNews.tags ? selectedNews.tags.split(',').slice(0, 3).map((tag: string, i: number) => (
                                                <span key={i} className="text-slate-300">{tag.trim()}{i < 2 && i < selectedNews.tags.split(',').length - 1 ? ',' : ''}</span>
                                            )) : <span className="text-slate-300">None</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* AI Summary Block */}
                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-5 shadow-lg">
                                <h3 className="text-[13px] font-bold text-white mb-3 tracking-wide">AI Summary</h3>
                                <div className="text-[12px] text-slate-400 font-sans leading-relaxed whitespace-pre-wrap">
                                    {selectedNews.ai_summary || selectedNews.summary || 'No detailed summary generated for this intercept.'}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 mt-2">
                                <button
                                    onClick={() => setSelectedNews(null)}
                                    className="px-5 py-2.5 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[12px] font-bold tracking-wide transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        if (selectedNews.link) setPendingUrl(selectedNews.link);
                                    }}
                                    className="px-5 py-2.5 rounded-lg bg-[#50A970] hover:bg-[#3d8556] text-black text-[12px] font-bold tracking-wide transition-colors"
                                >
                                    Read Full Article
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EXTERNAL URL REDIRECTION WARNING MODAL (OPSEC) --- */}
            {pendingUrl && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 ml-72">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(255,51,51,0.15)] rounded-2xl w-full max-w-[440px] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6">

                        {/* Red Concentric Pattern */}
                        <svg className="absolute -top-4 -left-4 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.05] pointer-events-none" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="30" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="20" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="10" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                        </svg>

                        <button onClick={() => { setPendingUrl(null); setCopied(false); }} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-12 h-12 border border-[#FF3333]/30 bg-[#FF3333]/10 rounded-xl flex items-center justify-center mb-5 relative z-10 shadow-inner">
                            <AlertTriangle className="w-5 h-5 text-[#FF3333]" />
                        </div>

                        <h2 className="text-[18px] font-sans font-bold text-white mb-3 relative z-10">External Redirection Warning</h2>
                        <p className="text-[12px] text-slate-400 font-mono leading-relaxed mb-5 relative z-10">
                            You are about to leave the platform and visit an external URL. This link may lead to a third-party news source, a leak site, or an unverified domain.
                        </p>

                        {/* UPDATED: Copy Button Block */}
                        <div className="bg-[#12151C] border border-[#FF3333]/30 rounded-lg p-3.5 mb-5 relative z-10 flex items-center gap-3 overflow-hidden shadow-inner">
                            <p className="text-[12px] text-[#FF6B6B] font-mono truncate flex-1" title={pendingUrl}>{pendingUrl}</p>
                            <button
                                onClick={() => copyToClipboard(pendingUrl)}
                                className="text-slate-500 hover:text-white transition-colors shrink-0 p-1"
                                title="Copy URL"
                            >
                                {copied ? <CheckCircle2 className="w-4 h-4 text-[#50A970]" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>

                        <p className="text-[11px] text-slate-500 font-mono leading-relaxed mb-6 relative z-10">
                            Security Tip: We recommend opening this link in a sandboxed environment or a dedicated VM.
                        </p>

                        <div className="flex justify-end gap-3 relative z-10">
                            <button
                                onClick={() => { setPendingUrl(null); setCopied(false); }}
                                className="px-5 py-2.5 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[12px] font-bold tracking-wide transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    window.open(pendingUrl, '_blank', 'noopener,noreferrer');
                                    setPendingUrl(null);
                                    setCopied(false);
                                }}
                                className="px-5 py-2.5 rounded-lg bg-[#DE3535] hover:bg-[#FF4444] text-white text-[12px] font-bold tracking-wide transition-colors"
                            >
                                Proceed (Unsafe)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </div>
    );
};

export default PublicNews;